import React, { Suspense, lazy, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { LanguageProvider } from "./context/LanguageContext";
import { ThemeProvider, useTheme } from "./context/ThemeContext";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import { OneSignalProvider } from "./services/OneSignalProvider";

// Admin-only shell (Command Palette, Joyride tours, Smart Guardrails, Certification)
// is lazy-loaded so public visitors never download the CRM/admin bundle.
const AdminShell = lazy(() => import("./components/common/AdminShell"));

// Loading spinner for lazy-loaded components - light fintech theme
const PageLoader = () => (
  <div className="min-h-screen bg-slate-50 flex items-center justify-center">
    <div className="text-center">
      <div className="w-8 h-8 border-2 border-[#00B050] border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
      <p className="text-slate-500 text-sm">Loading...</p>
    </div>
  </div>
);

// Minimal loader for quick transitions
const MinimalLoader = () => (
  <div className="min-h-[200px] flex items-center justify-center">
    <div className="w-6 h-6 border-2 border-[#00B050] border-t-transparent rounded-full animate-spin"></div>
  </div>
);

// Re-applies the correct theme on EVERY client-side route change (push/pop/back/forward),
// since ThemeProvider sits outside the Router and can't use useLocation itself.
function ThemeRouteSync() {
  const location = useLocation();
  const { applyThemeForCurrentPath } = useTheme();
  React.useEffect(() => {
    applyThemeForCurrentPath();
  }, [location.pathname, applyThemeForCurrentPath]);
  return null;
}

// PERFORMANCE: All public pages lazy loaded for smaller initial bundle
const HomePage = lazy(() => import("./pages/HomePage"));
const ServicesPage = lazy(() => import("./pages/ServicesPage"));
// ServiceDetailPage is deprecated - redirected via ServiceSlugRedirect
const ServiceSlugRedirect = lazy(() => import("./components/ServiceSlugRedirect"));
const LocationServicePage = lazy(() => import("./pages/LocationServicePage"));
const SEOLocationPage = lazy(() => import("./pages/SEOLocationPage"));

// Lazy load non-critical public pages
const ContactPage = lazy(() => import("./pages/ContactPage"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const CreditEducationPage = lazy(() => import("./pages/CreditEducationPage"));
const ArticlesPage = lazy(() => import("./pages/ArticlesPage"));
const ArticleDetailPage = lazy(() => import("./pages/ArticlesPage").then(module => ({ default: module.ArticleDetailPage })));
const EmailViewPage = lazy(() => import("./pages/EmailViewPage"));
// Legacy ApplyFundingPage removed - route redirects to /funnel/business-funding

// Lazy load Alayna AI chatbot (replaces old AIChatbot)
const LazyAlaynaChatbot = lazy(() => import("./components/alayna/LazyAlaynaChatbot"));

// PERFORMANCE: Admin pages - lazy load to reduce initial bundle
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const CommandCenter = lazy(() => import("./pages/admin/CommandCenter"));
const AdminLeads = lazy(() => import("./pages/admin/AdminLeads"));

// Lazy load other admin pages (not needed until user navigates there)
const AdminEmailCampaigns = lazy(() => import("./pages/admin/AdminEmailCampaigns"));
const AdminSMSCampaigns = lazy(() => import("./pages/admin/AdminSMSCampaigns"));
// Note: AdminSocialPosts (legacy) removed - redirects to AdminSocialMediaEnhanced
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
// Note: AdminTeam removed - legacy page, route redirects to AdminTeamManagement
const AdminWorkflows = lazy(() => import("./pages/admin/AdminWorkflows"));
const AdminSEO = lazy(() => import("./pages/admin/AdminSEO"));
const AdminNewsletter = lazy(() => import("./pages/admin/AdminNewsletter"));
const AdminBacklinks = lazy(() => import("./pages/admin/AdminBacklinks"));
const AdminEmailInbox = lazy(() => import("./pages/admin/AdminEmailInbox"));
const AdminSequences = lazy(() => import("./pages/admin/AdminSequences"));
const AdminCitations = lazy(() => import("./pages/admin/AdminCitations"));
const AdminBacklinkBuilder = lazy(() => import("./pages/admin/AdminBacklinkBuilder"));
const AdminLenders = lazy(() => import("./pages/admin/AdminLenders"));
const AdminPushNotifications = lazy(() => import("./pages/admin/AdminPushNotifications"));
const AdminABTesting = lazy(() => import("./pages/admin/AdminABTesting"));
const AdminPipeline = lazy(() => import("./pages/admin/AdminPipeline"));
const ServicePipelinePage = lazy(() => import("./pages/admin/ServicePipelinePage"));
const FollowUpQueuePage = lazy(() => import("./pages/admin/FollowUpQueuePage"));
const FollowUpAnalyticsPage = lazy(() => import("./pages/admin/FollowUpAnalyticsPage"));
const DigestSettingsPage = lazy(() => import("./pages/admin/DigestSettingsPage"));
const AlaynaImprovementsPage = lazy(() => import("./pages/admin/AlaynaImprovementsPage"));
const AlaynaDashboardPage = lazy(() => import("./pages/admin/AlaynaDashboardPage"));
const AlaynaTasksPage = lazy(() => import("./pages/admin/AlaynaTasksPage"));
const ServiceWorkspacePage = lazy(() => import("./pages/admin/ServiceWorkspacePage"));
const StageBackfillPage = lazy(() => import("./pages/admin/StageBackfillPage"));
const WorkflowAuditPage = lazy(() => import("./pages/admin/WorkflowAuditPage"));
const AdminCalendar = lazy(() => import("./pages/admin/AdminCalendar"));
const AdminSuggestions = lazy(() => import("./pages/admin/AdminSuggestions"));
const MarketingDashboard = lazy(() => import("./pages/admin/MarketingDashboard"));
const MarketingAnalyticsDashboard = lazy(() => import("./pages/admin/MarketingAnalyticsDashboard"));
const LifecycleDashboard = lazy(() => import("./pages/admin/LifecycleDashboard"));
const AdminSalesManagement = lazy(() => import("./pages/admin/AdminSalesManagement"));
const AIAutomations = lazy(() => import("./pages/admin/AIAutomations"));
const MyDashboard = lazy(() => import("./pages/admin/MyDashboard"));

// New RBAC pages
const AdminTeamManagement = lazy(() => import("./pages/admin/AdminTeamManagement"));
const AdminSalesDashboard = lazy(() => import("./pages/admin/AdminSalesDashboard"));
const AdminExpenses = lazy(() => import("./pages/admin/AdminExpenses"));
const AdminProfitLoss = lazy(() => import("./pages/admin/AdminProfitLoss"));
const AdminFinancialAnalytics = lazy(() => import("./pages/admin/AdminFinancialAnalytics"));
const VendorExpenseDashboard = lazy(() => import("./pages/admin/VendorExpenseDashboard"));
const AdminTaxDocuments = lazy(() => import("./pages/admin/AdminTaxDocuments"));
const AdminESignature = lazy(() => import("./pages/admin/AdminESignature"));
const DocuSealAnalyticsDashboard = lazy(() => import("./pages/admin/DocuSealAnalyticsDashboard"));
const AdminBanking = lazy(() => import("./pages/admin/AdminBanking"));
const AdminPayouts = lazy(() => import("./pages/admin/AdminPayouts"));
const AdminCommissionReports = lazy(() => import("./pages/admin/AdminCommissionReports"));
const AdminInternalChat = lazy(() => import("./pages/admin/AdminInternalChat"));
const AdminSocialMedia = lazy(() => import("./pages/admin/AdminSocialMediaEnhanced"));
// Note: AdminTraining (TrainingPage) removed - replaced by CertificationCenter, route redirects
const AdminAICalling = lazy(() => import("./pages/admin/AdminAICalling"));
const AICampaignsPage = lazy(() => import("./pages/admin/AICampaignsPage"));
const CampaignPerformanceDashboard = lazy(() => import("./pages/admin/CampaignPerformanceDashboard"));
// Note: LocalBusinessLeadEngine removed - never routed, orphaned code
// Note: SocialProspectingEngine deprecated - redirects to Social Automation
const OnboardingPage = lazy(() => import("./pages/admin/OnboardingPage"));
// Note: SalesDashboard (/admin/my-sales) removed June 2026 — orphaned duplicate of the
// rep dashboard. Route now redirects to canonical /admin/my-dashboard.
const ResetPassword = lazy(() => import("./pages/admin/ResetPassword"));
const AcceptInvite = lazy(() => import("./pages/admin/AcceptInvite"));
const ForgotPassword = lazy(() => import("./pages/admin/ForgotPassword"));
const PayInvoicePage = lazy(() => import("./pages/public/PayInvoicePage"));
const UploadDocsPage = lazy(() => import("./pages/public/UploadDocsPage"));
const FunnelOptimization = lazy(() => import("./pages/admin/FunnelOptimization"));
const FunnelAnalyticsDashboard = lazy(() => import("./pages/admin/FunnelAnalyticsDashboard"));
const LeadQualityQueue = lazy(() => import("./pages/admin/LeadQualityQueue"));
const AffiliateForms = lazy(() => import("./pages/admin/AffiliateForms"));
const AdminSubmissions = lazy(() => import("./pages/admin/AdminSubmissions"));
const AdminCreditRepair = lazy(() => import("./pages/admin/AdminCreditRepair"));
const AffiliateAnnouncements = lazy(() => import("./pages/admin/AffiliateAnnouncements"));
const AffiliateReferrals = lazy(() => import("./pages/admin/AffiliateReferrals"));
const EmbedFormPage = lazy(() => import("./pages/public/EmbedFormPage"));
const AffiliateLogin = lazy(() => import("./pages/affiliate/AffiliateLogin"));
const AffiliateVerify = lazy(() => import("./pages/affiliate/AffiliateVerify"));
const AffiliatePortal = lazy(() => import("./pages/affiliate/AffiliatePortal"));
const AffiliateApply = lazy(() => import("./pages/affiliate/AffiliateApply"));

// Short branded referral link: /r/:code -> /affiliate/apply?ref=:code (tracking preserved).
function ReferralRedirect() {
  const { code } = useParams();
  useEffect(() => {
    if (!code) return;
    try {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/referral/click`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code }),
        keepalive: true,
      }).catch(() => {});
    } catch (e) { /* non-blocking */ }
  }, [code]);
  return <Navigate to={`/affiliate/apply?ref=${encodeURIComponent(code || "")}`} replace />;
}
const OfferAcceptPage = lazy(() => import("./pages/public/OfferAcceptPage"));
const FundingFunnelDashboard = lazy(() => import("./pages/admin/FundingFunnelDashboard"));
const EmailAnalytics = lazy(() => import("./pages/admin/EmailAnalytics"));
const AdminLandingPages = lazy(() => import("./pages/admin/AdminLandingPages"));
const AdminAlaynaConfig = lazy(() => import("./pages/admin/AdminAlaynaConfig"));
const AlaynaOutboundControlPanel = lazy(() => import("./pages/admin/AlaynaOutboundControlPanel"));
const AdminLeadAssignment = lazy(() => import("./pages/admin/AdminLeadAssignment"));
const LeadImportCampaignEngine = lazy(() => import("./pages/admin/LeadImportCampaignEngine"));
const ProspectDiscoveryPage = lazy(() => import("./pages/admin/ProspectDiscoveryPage"));
const DiscoveryROIDashboard = lazy(() => import("./pages/admin/DiscoveryROIDashboard"));
const AffiliateCommissions = lazy(() => import("./pages/admin/AffiliateCommissions"));
const AdminSMSCenter = lazy(() => import("./pages/admin/AdminSMSCenter"));
const CertificationCenter = lazy(() => import("./pages/admin/CertificationCenter"));
const CommissionEngineDashboard = lazy(() => import("./pages/admin/CommissionEngineDashboard"));
const SystemHealthDashboard = lazy(() => import("./pages/admin/SystemHealthDashboard"));
const TasksPage = lazy(() => import("./pages/admin/TasksPage"));
const DocumentsPage = lazy(() => import("./pages/admin/DocumentsPage"));
const RequalifyQueuePage = lazy(() => import("./pages/admin/RequalifyQueuePage"));
const AIAutoResponsePage = lazy(() => import("./pages/admin/AIAutoResponsePage"));
const MyProfilePage = lazy(() => import("./pages/admin/MyProfilePage"));
const IntakeSchemasPage = lazy(() => import("./pages/admin/IntakeSchemasPage"));
const FormScenariosAdminPage = lazy(() => import("./pages/admin/FormScenariosAdminPage"));

// Public Funnel Pages
const MCAFunnelPage = lazy(() => import("./pages/public/MCAFunnelPage"));
const CreditRepairFunnelPage = lazy(() => import("./pages/public/CreditRepairFunnelPage"));
const MerchantServicesFunnelPage = lazy(() => import("./pages/public/MerchantServicesFunnelPage"));

// Public Landing Pages
const BusinessFundingLandingPage = lazy(() => import("./pages/public/BusinessFundingLandingPage"));
const CreditRepairLandingPage = lazy(() => import("./pages/public/CreditRepairLandingPage"));

// Intake Forms
const MasterIntakePage = lazy(() => import("./pages/public/MasterIntakePage"));
// Removed services: Auto Insurance, Auto Broker, Tax Services — routes redirect to homepage
const ThankYouPage = lazy(() => import("./pages/public/ThankYouPage"));
const AffiliateIntakeEmbed = lazy(() => import("./pages/public/AffiliateIntakeEmbed"));

// Secure Application Pages (Multi-step secure intake)
const SecureApplicationPage = lazy(() => import("./pages/public/SecureApplicationPage"));
// Secure Application Token Page (Token-based application resumption)
const SecureApplicationTokenPage = lazy(() => import("./pages/public/SecureApplicationTokenPage"));

// Service Landing Pages (Restructured with secure application CTA)
const ServiceLandingPage = lazy(() => import("./pages/public/ServiceLandingPage"));

// Legal Pages
const PrivacyPolicyPage = lazy(() => import("./pages/public/PrivacyPolicyPage"));
const TermsAndConditionsPage = lazy(() => import("./pages/public/TermsAndConditionsPage"));

// Error boundary for lazy-loaded components
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center">
          <div className="text-center p-8 bg-white rounded-xl shadow-lg border border-slate-200">
            <h1 className="text-2xl mb-4 text-slate-900">Something went wrong</h1>
            <p className="text-slate-600 mb-4">{this.state.error?.message || 'Unknown error'}</p>
            <button 
              onClick={() => window.location.reload()} 
              className="bg-[#00B050] text-white px-4 py-2 rounded"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Admin/finance/marketing/automation-only areas that SALES reps must not open
// via direct URL. Operational pages (pipeline, tasks, follow-ups, lenders,
// workspaces, commissions, social, profile, calendar, chat, documents) stay open.
const SALES_DENY_PREFIXES = [
  '/admin/command-center', '/admin/lifecycle-dashboard',
  '/admin/financial-analytics', '/admin/profit-loss', '/admin/banking', '/admin/payouts',
  '/admin/vendor-expenses', '/admin/tax-documents', '/admin/commission-engine', '/admin/docuseal-analytics',
  '/admin/finance', '/admin/expenses',
  '/admin/settings', '/admin/team-management', '/admin/lead-assignment', '/admin/system-health',
  '/admin/affiliate-forms', '/admin/affiliate-announcements', '/admin/affiliate-referrals',
  '/admin/digest-settings', '/admin/sales-management', '/admin/stage-backfill', '/admin/workflow-audit',
  '/admin/marketing-dashboard', '/admin/marketing-analytics', '/admin/email-campaigns', '/admin/email-inbox',
  '/admin/email-analytics', '/admin/ai-campaigns', '/admin/campaign-analytics', '/admin/newsletter',
  '/admin/sequences', '/admin/sms', '/admin/push-notifications', '/admin/seo', '/admin/citations',
  '/admin/backlinks', '/admin/landing-pages', '/admin/funnel-optimization', '/admin/funnel-analytics',
  '/admin/funding-funnel', '/admin/follow-up-analytics', '/admin/ab-testing', '/admin/affiliate-forms',
  '/admin/intake-schemas', '/admin/form-scenarios', '/admin/lead-quality', '/admin/service-pipeline',
  '/admin/ai-automations', '/admin/ai-auto-response', '/admin/ai-calling', '/admin/alayna-config',
  '/admin/alayna-dashboard', '/admin/alayna-improvements', '/admin/alayna-outbound-control',
  '/admin/alayna-tasks', '/admin/lead-engine', '/admin/prospect-discovery', '/admin/outbound-control', '/admin/discovery-roi',
  '/admin/exchange-network', '/admin/e-signature',
];

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = window.location.pathname;
  const [certificationChecked, setCertificationChecked] = React.useState(false);
  const [isCertified, setIsCertified] = React.useState(true);
  const [showTour, setShowTour] = React.useState(false);
  
  // All users can access CRM - Training Center is optional self-paced
  const checkCertification = React.useCallback(async () => {
    // All roles can access CRM without certification
    // Training Center is available for self-paced learning
    setIsCertified(true);
    
    // Check if first time login - show tour
    const tourSeen = localStorage.getItem('crm_tour_seen');
    const justCertified = localStorage.getItem('just_certified');
    
    if (!tourSeen || justCertified === 'true') {
      // Show tour for new users
      if (user?.role === 'sales' || user?.role === 'marketing') {
        setShowTour(true);
        localStorage.setItem('crm_tour_seen', 'true');
      }
      localStorage.removeItem('just_certified');
      localStorage.removeItem('just_completed_onboarding');
    }
    
    setCertificationChecked(true);
  }, [user?.role]);
  
  React.useEffect(() => {
    if (user && !certificationChecked) {
      checkCertification();
    }
  }, [user, certificationChecked, checkCertification]);
  
  // Re-check certification when navigating away from onboarding page (sales only)
  React.useEffect(() => {
    if (user?.role === 'sales') {
      const wasOnOnboarding = localStorage.getItem('was_on_onboarding');
      if (wasOnOnboarding === 'true' && !location.includes('/admin/onboarding')) {
        localStorage.removeItem('was_on_onboarding');
        setCertificationChecked(false);
      }
      
      if (location.includes('/admin/onboarding')) {
        localStorage.setItem('was_on_onboarding', 'true');
      }
    }
  }, [location, user?.role]);
  
  if (loading || ((user?.role === 'sales' || user?.role === 'marketing') && !certificationChecked)) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#00B050] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/admin/login" replace />;
  }
  
  // Redirect uncertified SALES agents to onboarding (marketing skips this)
  if (user.role === 'sales' && !isCertified && !location.includes('/admin/onboarding')) {
    return <Navigate to="/admin/onboarding" replace />;
  }

  // Role guard: block SALES reps from admin/finance/marketing/automation-only pages
  // via direct URL (prevents the blank-page state). Operational pages stay accessible.
  if (user.role === 'sales' && SALES_DENY_PREFIXES.some(p => location.startsWith(p))) {
    toast.error("You don't have access to that page.");
    return <Navigate to="/admin/my-dashboard" replace />;
  }
  
  // Pass showTour and setShowTour to children via context/props
  return React.cloneElement(children, { showTour, setShowTour });
};

// Role-based dashboard redirect component
const AdminDashboardRedirect = () => {
  const { user } = useAuth();
  
  // Admin/Super Admin → Command Center
  if (user?.role === 'admin' || user?.role === 'super_admin') {
    return <Navigate to="/admin/command-center" replace />;
  }
  
  // Sales agents → Agent Dashboard (My Dashboard)
  if (user?.role === 'sales') {
    return <Navigate to="/admin/my-dashboard" replace />;
  }
  
  // Marketing → Campaign Manager
  if (user?.role === 'marketing') {
    return <Navigate to="/admin/marketing-dashboard" replace />;
  }
  
  // Accounting → Financial Analytics
  if (user?.role === 'accounting') {
    return <Navigate to="/admin/financial-analytics" replace />;
  }
  
  // Fallback to Command Center for any other roles
  return <Navigate to="/admin/command-center" replace />;
};

function AppRoutes() {
  return (
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Redirects for old URLs (301 equivalent via Navigate) */}
            <Route path="/home-old" element={<Navigate to="/" replace />} />
            <Route path="/home-old/" element={<Navigate to="/" replace />} />
            <Route path="/tmp/*" element={<Navigate to="/" replace />} />
            
            {/* Public Routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/services" element={<ServicesPage />} />
            {/* Legacy /services/:slug redirects to new /service/:serviceType */}
            <Route path="/services/:slug" element={<ServiceSlugRedirect />} />
            <Route path="/locations/:location/:service" element={<LocationServicePage />} />
            
            {/* SEO Location Pages - Service + City */}
            <Route path="/business-funding/:city" element={<SEOLocationPage />} />
            {/* SEO Location Pages - only active services */}
            <Route path="/credit-repair/:city" element={<SEOLocationPage />} />
            <Route path="/merchant-credit-cards/:city" element={<SEOLocationPage />} />
            {/* Removed service location pages redirect to home */}
            <Route path="/tax-services/:city" element={<Navigate to="/" replace />} />
            <Route path="/auto-insurance/:city" element={<Navigate to="/" replace />} />
            <Route path="/auto-broker/:city" element={<Navigate to="/" replace />} />
            
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/credit-education" element={<CreditEducationPage />} />
            <Route path="/articles" element={<ArticlesPage />} />
            <Route path="/articles/:slug" element={<ArticleDetailPage />} />
            <Route path="/email-view" element={<EmailViewPage />} />
            
            {/* Legal Pages */}
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/privacy" element={<Navigate to="/privacy-policy" replace />} />
            <Route path="/terms" element={<TermsAndConditionsPage />} />
            <Route path="/terms-and-conditions" element={<Navigate to="/terms" replace />} />
            
            {/* Redirect legacy /apply/funding to proper funnel */}
            <Route path="/apply/funding" element={<Navigate to="/funnel/business-funding" replace />} />
            
            {/* Public Funnel Pages - Active services only */}
            <Route path="/funnel/business-funding" element={<MCAFunnelPage />} />
            {/* Public affiliate embed lead-capture form */}
            <Route path="/embed/:service" element={<Suspense fallback={<PageLoader />}><EmbedFormPage /></Suspense>} />
            {/* Affiliate Portal (passwordless magic-link) */}
            <Route path="/affiliate/login" element={<Suspense fallback={<PageLoader />}><AffiliateLogin /></Suspense>} />
            <Route path="/affiliate/verify" element={<Suspense fallback={<PageLoader />}><AffiliateVerify /></Suspense>} />
            <Route path="/affiliate/portal" element={<Suspense fallback={<PageLoader />}><AffiliatePortal /></Suspense>} />
            <Route path="/affiliate/apply" element={<Suspense fallback={<PageLoader />}><AffiliateApply /></Suspense>} />
            <Route path="/r/:code" element={<ReferralRedirect />} />
            <Route path="/funnel/mca" element={<MCAFunnelPage />} />
            <Route path="/funnel/credit-repair" element={<CreditRepairFunnelPage />} />
            {/* Merchant Services funnel - primary service equal to Funding & Credit */}
            <Route path="/funnel/merchant-services" element={<MerchantServicesFunnelPage />} />
            <Route path="/funnel/merchant-processing" element={<Navigate to="/funnel/merchant-services" replace />} />
            {/* Removed service funnels redirect to homepage */}
            <Route path="/funnel/auto-insurance" element={<Navigate to="/" replace />} />
            <Route path="/funnel/tax-services" element={<Navigate to="/" replace />} />
            <Route path="/funnel/auto-broker" element={<Navigate to="/" replace />} />
            
            {/* Public Landing Pages (for ads) */}
            <Route path="/lp/business-funding" element={<BusinessFundingLandingPage />} />
            <Route path="/lp/credit-repair" element={<CreditRepairLandingPage />} />
            
            {/* Intake Forms */}
            <Route path="/intake" element={<MasterIntakePage />} />
            {/* Deprecated intake routes - redirect to home */}
            <Route path="/intake/tax" element={<Navigate to="/" replace />} />
            <Route path="/intake/insurance" element={<Navigate to="/" replace />} />
            <Route path="/intake/auto" element={<Navigate to="/" replace />} />
            <Route path="/thank-you" element={<ThankYouPage />} />
            
            {/* Legacy apply routes - redirect to funnel architecture */}
            <Route path="/apply/business-funding" element={<Navigate to="/funnel/business-funding" replace />} />
            <Route path="/apply/credit-repair" element={<Navigate to="/funnel/credit-repair" replace />} />
            <Route path="/apply/auto-insurance" element={<Navigate to="/" replace />} />
            <Route path="/apply/auto-broker" element={<Navigate to="/" replace />} />
            <Route path="/apply/tax-services" element={<Navigate to="/" replace />} />
            <Route path="/apply/merchant-processing" element={<Navigate to="/funnel/merchant-services" replace />} />
            <Route path="/apply/merchant-services" element={<Navigate to="/funnel/merchant-services" replace />} />
            {/* Catch-all for any other service type */}
            <Route path="/apply/:serviceType" element={<Navigate to="/intake" replace />} />
            
            {/* Direct Full Application (P4) — rep/affiliate sent, no funnel, no Alayna */}
            <Route path="/application/:serviceType" element={
              <Suspense fallback={<PageLoader />}>
                <SecureApplicationPage />
              </Suspense>
            } />
            
            {/* Secure Application Token Page - For resuming applications via secure link */}
            <Route path="/secure-application/:token" element={
              <Suspense fallback={<PageLoader />}>
                <SecureApplicationTokenPage />
              </Suspense>
            } />
            
            {/* Service Landing Pages - Restructured with secure application CTA */}
            {/* Legacy service landing pages redirect to closest active service (Service Consolidation) */}
            <Route path="/service/tax-services" element={<Navigate to="/service/business-funding" replace />} />
            <Route path="/service/auto-insurance" element={<Navigate to="/services" replace />} />
            <Route path="/service/auto-broker" element={<Navigate to="/services" replace />} />
            <Route path="/service/insurance" element={<Navigate to="/services" replace />} />
            <Route path="/service/:serviceType" element={<ServiceLandingPage />} />
            
            {/* Affiliate Embedded Intake Form (Public - no auth) */}
            <Route path="/embed/intake" element={
              <Suspense fallback={<PageLoader />}>
                <AffiliateIntakeEmbed />
              </Suspense>
            } />
            
            {/* Public Payment Page */}
            <Route path="/pay/:token" element={<PayInvoicePage />} />

            {/* Public one-tap Offer Acceptance */}
            <Route path="/offer/:token" element={<OfferAcceptPage />} />

            {/* Public Secure Post-Offer Document Upload */}
            <Route path="/upload-docs/:lead_id/:token" element={<UploadDocsPage />} />
            
            {/* Admin Routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            
            {/* Command Center - Primary Admin Dashboard */}
            <Route path="/admin/command-center" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <CommandCenter />
                </Suspense>
              </ProtectedRoute>
            } />
            
            {/* Redirect legacy /admin to Command Center for admins */}
            <Route path="/admin" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminDashboardRedirect />
                </Suspense>
              </ProtectedRoute>
            } />
            
            {/* Legacy CEO Dashboard - redirect to Command Center */}
            <Route path="/admin/ceo-dashboard" element={
              <Navigate to="/admin/command-center" replace />
            } />
            <Route path="/admin/commission-engine" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <CommissionEngineDashboard />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/tasks" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <TasksPage />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/documents" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <DocumentsPage />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/my-dashboard" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <MyDashboard />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/leads" element={
              <ProtectedRoute>
                <AdminLeads />
              </ProtectedRoute>
            } />
            <Route path="/admin/pipeline" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminPipeline />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/service-pipeline" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <ServicePipelinePage />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/follow-ups" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <FollowUpQueuePage />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/follow-up-analytics" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <FollowUpAnalyticsPage />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/digest-settings" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <DigestSettingsPage />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/alayna-improvements" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AlaynaImprovementsPage />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/alayna-dashboard" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AlaynaDashboardPage />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/alayna-tasks" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AlaynaTasksPage />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/workspace/:service" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <ServiceWorkspacePage />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/stage-backfill" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <StageBackfillPage />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/workflow-audit" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <WorkflowAuditPage />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/requalify-queue" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <RequalifyQueuePage />
                </Suspense>
              </ProtectedRoute>
            } />
            {/* Legacy route redirects */}
            <Route path="/admin/mca-leads" element={<Navigate to="/admin/lead-engine/imports-campaigns" replace />} />
            <Route path="/admin/calendar" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminCalendar />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/marketing-dashboard" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <MarketingDashboard />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/marketing-analytics" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <MarketingAnalyticsDashboard />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/lifecycle-dashboard" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <LifecycleDashboard />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/sales-management" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminSalesManagement />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/ai-automations" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AIAutomations />
                </Suspense>
              </ProtectedRoute>
            } />
            {/* Legacy route - redirect to canonical funnel-optimization */}
            <Route path="/admin/funnels" element={<Navigate to="/admin/funnel-optimization" replace />} />
            <Route path="/admin/funnel-optimization" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <FunnelOptimization />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/funnel-analytics" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <FunnelAnalyticsDashboard />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/lead-quality" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <LeadQualityQueue />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/affiliate-forms" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AffiliateForms />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/affiliate-submissions" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminSubmissions />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/affiliate-announcements" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AffiliateAnnouncements />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/affiliate-referrals" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AffiliateReferrals />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/funding-funnel" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <FundingFunnelDashboard />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/intake-schemas" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <IntakeSchemasPage />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/form-scenarios" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <FormScenariosAdminPage />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/landing-pages" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminLandingPages />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/alayna-config" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminAlaynaConfig />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/alayna-outbound-control" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AlaynaOutboundControlPanel />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/ai-auto-response" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AIAutoResponsePage />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/lead-assignment" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminLeadAssignment />
                </Suspense>
              </ProtectedRoute>
            } />
            {/* Legacy cold-mca redirect */}
            <Route path="/admin/cold-mca" element={<Navigate to="/admin/lead-engine/imports-campaigns" replace />} />
            <Route path="/admin/sms" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminSMSCenter />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/email-analytics" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <EmailAnalytics />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/newsletter" element={
              <ProtectedRoute>
                <AdminNewsletter />
              </ProtectedRoute>
            } />
            <Route path="/admin/email-campaigns" element={
              <ProtectedRoute>
                <AdminEmailCampaigns />
              </ProtectedRoute>
            } />
            <Route path="/admin/sms-campaigns" element={
              <ProtectedRoute>
                <AdminSMSCampaigns />
              </ProtectedRoute>
            } />
            {/* Legacy social-posts route redirects to new Make-based social page */}
            <Route path="/admin/social-posts" element={<Navigate to="/admin/social-media" replace />} />
            <Route path="/admin/settings" element={
              <ProtectedRoute>
                <AdminSettings />
              </ProtectedRoute>
            } />
            {/* Legacy route - redirect to canonical team-management */}
            <Route path="/admin/team" element={<Navigate to="/admin/team-management" replace />} />
            <Route path="/admin/workflows" element={
              <ProtectedRoute>
                <AdminWorkflows />
              </ProtectedRoute>
            } />
            <Route path="/admin/seo" element={
              <ProtectedRoute>
                <AdminSEO />
              </ProtectedRoute>
            } />
            <Route path="/admin/backlinks" element={
              <ProtectedRoute>
                <AdminBacklinks />
              </ProtectedRoute>
            } />
            <Route path="/admin/email-inbox" element={<Navigate to="/admin/email-campaigns" replace />} />
            <Route path="/admin/sequences" element={
              <ProtectedRoute>
                <AdminSequences />
              </ProtectedRoute>
            } />
            <Route path="/admin/citations" element={
              <ProtectedRoute>
                <AdminCitations />
              </ProtectedRoute>
            } />
            <Route path="/admin/backlink-builder" element={
              <ProtectedRoute>
                <AdminBacklinkBuilder />
              </ProtectedRoute>
            } />
            <Route path="/admin/lenders" element={
              <ProtectedRoute>
                <AdminLenders />
              </ProtectedRoute>
            } />
            <Route path="/admin/push-notifications" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminPushNotifications />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/ab-testing" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminABTesting />
                </Suspense>
              </ProtectedRoute>
            } />
            
            {/* New RBAC Routes */}
            <Route path="/admin/team-management" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminTeamManagement />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/sales" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminSalesDashboard />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/expenses" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminExpenses />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/profit-loss" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminProfitLoss />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/financial-analytics" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminFinancialAnalytics />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/vendor-expenses" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <VendorExpenseDashboard />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/tax-documents" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminTaxDocuments />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/e-signature" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminESignature />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/docuseal-analytics" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <DocuSealAnalyticsDashboard />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/banking" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminBanking />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/payouts" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminPayouts />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/commission-reports" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminCommissionReports />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/chat" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminInternalChat />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/suggestions" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminSuggestions />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/social-media" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminSocialMedia />
                </Suspense>
              </ProtectedRoute>
            } />
            {/* Legacy lead-engine/business redirect */}
            <Route path="/admin/lead-engine/business" element={<Navigate to="/admin/lead-engine/imports-campaigns" replace />} />
            {/* Legacy social prospecting redirects to Social Automation */}
            <Route path="/admin/lead-engine/social" element={<Navigate to="/admin/social-media" replace />} />
            <Route path="/admin/lead-engine/imports-campaigns" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <LeadImportCampaignEngine />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/lead-engine/prospect-discovery" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <ProspectDiscoveryPage />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/lead-engine/discovery-roi" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <DiscoveryROIDashboard />
                </Suspense>
              </ProtectedRoute>
            } />
            {/* Consolidated June 2026: profiles & commissions now live in the unified Affiliates page */}
            <Route path="/admin/lead-engine/affiliates" element={<Navigate to="/admin/affiliate-forms?tab=profiles" replace />} />
            <Route path="/admin/finance/commissions" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AffiliateCommissions />
                </Suspense>
              </ProtectedRoute>
            } />
            {/* Legacy training route redirects to Certification Center */}
            <Route path="/admin/training" element={<Navigate to="/admin/certification" replace />} />
            <Route path="/admin/ai-calling" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminAICalling />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/ai-campaigns" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AICampaignsPage />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/campaign-analytics" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <CampaignPerformanceDashboard />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/onboarding" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <OnboardingPage />
                </Suspense>
              </ProtectedRoute>
            } />
            {/* Legacy /admin/my-sales — orphaned duplicate, redirect to canonical rep dashboard */}
            <Route path="/admin/my-sales" element={<Navigate to="/admin/my-dashboard" replace />} />
            <Route path="/admin/certification" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <CertificationCenter />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/my-profile" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <MyProfilePage />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/system-health" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <SystemHealthDashboard />
                </Suspense>
              </ProtectedRoute>
            } />
            
            {/* Auth Routes (no protection) */}
            <Route path="/admin/reset-password" element={
              <Suspense fallback={<PageLoader />}>
                <ResetPassword />
              </Suspense>
            } />
            <Route path="/admin/accept-invite" element={
              <Suspense fallback={<PageLoader />}>
                <AcceptInvite />
              </Suspense>
            } />
            <Route path="/admin/forgot-password" element={
              <Suspense fallback={<PageLoader />}>
                <ForgotPassword />
              </Suspense>
            } />
          </Routes>
        </Suspense>
  );
}

// Admin auth pages stay lightweight (no CRM/admin shell, no react-joyride etc.)
const ADMIN_AUTH_PATHS = [
  "/admin/login",
  "/admin/forgot-password",
  "/admin/reset-password",
  "/admin/accept-invite",
];

function ShellRouter() {
  const { pathname } = useLocation();
  const needsAdminShell =
    pathname.startsWith("/admin") && !ADMIN_AUTH_PATHS.includes(pathname);

  if (needsAdminShell) {
    return (
      <Suspense fallback={<PageLoader />}>
        <AdminShell>
          <AppRoutes />
        </AdminShell>
      </Suspense>
    );
  }
  return <AppRoutes />;
}

function App() {
  return (
    <OneSignalProvider>
    <ThemeProvider>
    <AuthProvider>
      <BrowserRouter>
        <LanguageProvider>
          {/* Skip to main content link for accessibility */}
          <a href="#main-content" className="skip-link">
            Skip to main content
          </a>
          <ErrorBoundary>
            <ThemeRouteSync />
            <ShellRouter />
            {/* Alayna AI chatbot - light fintech theme, conversion-safe */}
            <Suspense fallback={null}>
              <LazyAlaynaChatbot />
            </Suspense>
            <Toaster position="top-right" richColors />
          </ErrorBoundary>
        </LanguageProvider>
      </BrowserRouter>
    </AuthProvider>
    </ThemeProvider>
    </OneSignalProvider>
  );
}

export default App;
